import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Inbox, MessageSquare } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { parseAppCode } from '@/lib/utils/url';
import { INBOX_SUPPORTED_TPAS } from '@/config/constants';
import { LoginForm } from '@/components/auth/login-form';

function formatAppLabel(appCode: string): { tpa: string; saas: string } {
  const parsed = parseAppCode(appCode);
  if (!parsed) return { tpa: appCode, saas: '' };

  const capitalize = (s: string) =>
    s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return { tpa: capitalize(parsed.tpa), saas: capitalize(parsed.saas) };
}

export function InboxPickerPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);

  // Initialize auth listener
  useEffect(() => {
    const unsubscribe = useAuthStore.getState().initializeAuth();
    return unsubscribe;
  }, []);

  const installedApps = user?.inAppCodes ?? [];

  // Filter to only inbox-supported apps
  const inboxApps = useMemo(() => {
    return installedApps.filter((appCode) => {
      const parsed = parseAppCode(appCode);
      return parsed && INBOX_SUPPORTED_TPAS.has(parsed.tpa);
    });
  }, [installedApps]);

  // Auto-redirect if exactly one inbox app
  useEffect(() => {
    if (user && inboxApps.length === 1) {
      navigate(`/inbox/${inboxApps[0]}`, { replace: true });
    }
  }, [user, inboxApps, navigate]);

  // Not initialized yet — show nothing (avoid flash)
  if (!initialized) return null;

  // Not logged in — show embedded login
  if (!user) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <header className="border-b border-border/60 bg-card/95 px-6 py-3">
          <h2 className="text-lg font-semibold text-foreground">Ulgebra</h2>
        </header>
        <div className="flex flex-1 items-center justify-center p-8">
          <LoginForm />
        </div>
      </div>
    );
  }

  // While redirecting for single app, show nothing
  if (inboxApps.length === 1) return null;

  // No inbox apps installed
  if (inboxApps.length === 0) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <header className="border-b border-border/60 bg-card/95 px-6 py-3">
          <h2 className="text-lg font-semibold text-foreground">Ulgebra</h2>
        </header>
        <div className="flex flex-1 items-center justify-center">
          <div className="max-w-sm text-center space-y-4 px-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Inbox className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">No Inbox Apps</h2>
            <p className="text-sm text-muted-foreground">
              You don't have any apps installed that support the inbox view.
              Install an app like Twilio, WhatsApp, or Intercom to get started.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Multiple inbox apps — show picker
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border/60 bg-card/95 px-6 py-3">
        <h2 className="text-lg font-semibold text-foreground">Ulgebra</h2>
      </header>
      <div className="flex flex-1 items-center justify-center">
        <div className="w-full max-w-sm space-y-4 px-6">
          <div className="text-center space-y-1">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 mb-3">
              <MessageSquare className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Select Inbox</h2>
            <p className="text-sm text-muted-foreground">Choose which app's inbox to open</p>
          </div>
          <div className="space-y-2">
            {inboxApps.map((appCode) => {
              const { tpa, saas } = formatAppLabel(appCode);
              return (
                <button
                  key={appCode}
                  onClick={() => navigate(`/inbox/${appCode}`)}
                  className="w-full flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-left hover:bg-accent transition-colors group"
                >
                  <div>
                    <span className="text-sm font-medium text-foreground">{tpa}</span>
                    {saas && (
                      <span className="text-sm text-muted-foreground"> for {saas}</span>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
